import { useMemo } from 'react';

import { UnauthorizedJobReason } from '@xing-com/crate-common-graphql-types';
import { useParams } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { JobNotAvailable } from '@xing-com/crate-jobs-domain-job-details-components-error';
import { selectJobDetails } from '@xing-com/crate-jobs-helpers';
import {
  useJobDetails,
  useJobMatchingSkills,
  useUserJobPreferences,
  useJobDetailsLinkBoxes,
} from '@xing-com/crate-jobs-hooks';
import { NoSSRLoggedInSuspense } from '@xing-com/crate-utils-no-ssr';
import { useHost } from '@xing-com/crate-xinglet';

import { useIsFencedJob } from '../../hooks';
import { getJobRoleFromSlug } from '../linkboxes-module/utils';

import { JobDetailsContent } from './job-details-content';
import { JobDetailsLoading } from './job-details-loading';

export const JobDetails: React.FC = () => (
  <NoSSRLoggedInSuspense fallback={<JobDetailsLoading />}>
    <JobDetailsInner />
  </NoSSRLoggedInSuspense>
);

export const JobDetailsInner: React.FC = () => {
  const host = useHost();
  const { slug } = useParams<{ slug: string }>();
  const { job, error } = useJobDetails(slug);
  const { isLoggedIn } = useLoginState();
  const isFencedJob = useIsFencedJob(job);
  const jobRole = useMemo(() => getJobRoleFromSlug(slug), [slug]);
  const { linkboxes } = useJobDetailsLinkBoxes({
    skip: isFencedJob,
    jobRole,
    location: job?.location?.city || '',
  });
  const displaySkillsAndPreferences =
    isLoggedIn && job && !job.redirectsToThirdPartyUrl;
  const { data: userJobPreferences } = useUserJobPreferences({
    skip: !displaySkillsAndPreferences || isFencedJob,
  });
  const { matchedSkills, notMatchedSkills } = useJobMatchingSkills({
    slug,
    skip: !displaySkillsAndPreferences || isFencedJob,
  });

  if (!job) {
    if (error === UnauthorizedJobReason.Private && !isLoggedIn) {
      return host.redirectToLogin();
    } else {
      return <JobNotAvailable />;
    }
  } else if (isFencedJob) {
    return <JobNotAvailable />;
  }

  const jobDetails = selectJobDetails(job);

  return (
    <JobDetailsContent
      jobDetails={jobDetails}
      userJobPreferences={userJobPreferences}
      matchedSkills={matchedSkills}
      notMatchedSkills={notMatchedSkills}
      linkboxes={linkboxes}
      jobRole={jobRole}
    />
  );
};
